import React, { useContext } from "react";
import { IntlContext } from "context";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import FieldGroup, { FieldGroupProps } from "./Element/FieldGroup";
import Label, { LabelProps } from "./Element/Label";
import Input, { InputProps } from "./Element/Input";
import Feedback from "./Element/Feedback";
import Button from "ui/Button";
import { TextField, SelectCustom, Textarea, Checkbox, File, Radio } from "components/Form/Field";

type RepeaterInnerField = {
    key?: string;
    component: string;
    label?: React.ReactNode;
    type?: string;
    format?: unknown;
    // Allow passing through component-specific props without losing type safety elsewhere.
    [key: string]: unknown;
};

type ErrorCode = { code?: string };
type RepeaterErrorShape = {
    props?: {
        error?:
            | ErrorCode
            | Array<ErrorCode | Record<string, ErrorCode | undefined> | undefined>
            | Record<string, unknown>;
    };
};

type RepeaterFields<T> = {
    length?: number | undefined;
    map: <R>(cb: (name: string, index: number) => R) => R[];
    push: (value: T) => void;
    remove: (index: number) => void;
};

type RepeaterCustomFieldProps = {
    inputProps: Record<string, unknown>;
    labelProps: { label?: React.ReactNode };
    format?: unknown;
};

const customComponents: Record<string, React.ComponentType<RepeaterCustomFieldProps>> = {
    TextField: TextField as unknown as React.ComponentType<RepeaterCustomFieldProps>,
    SelectCustom: SelectCustom as unknown as React.ComponentType<RepeaterCustomFieldProps>,
    TextareaField: Textarea as unknown as React.ComponentType<RepeaterCustomFieldProps>,
    CheckboxField: Checkbox as unknown as React.ComponentType<RepeaterCustomFieldProps>,
    RadioField: Radio as unknown as React.ComponentType<RepeaterCustomFieldProps>,
    File: File as unknown as React.ComponentType<RepeaterCustomFieldProps>,
};

const FieldRepeaterItem: React.FC<{
    name: string;
    index: number;
    field: RepeaterInnerField;
    errorMessage?: RepeaterErrorShape;
    disabled: boolean;
}> = ({ name, index, field, errorMessage, disabled }) => {
    const intl = useContext(IntlContext);
    const CustomComponent = customComponents[field.component];
    const fieldError = errorMessage?.props?.error;
    const indexedError =
        Array.isArray(fieldError) && index >= 0 && index < fieldError.length ? fieldError[index] : undefined;
    const keyedError =
        typeof indexedError === "object" && indexedError !== null && field.key
            ? (indexedError as Record<string, unknown>)[field.key]
            : undefined;
    const hasError = keyedError !== undefined || (typeof indexedError === "object" && indexedError !== null);
    const fieldName = field.key ? `${name}.${field.key}` : name;
    const errorCode =
        typeof keyedError === "object" && keyedError !== null
            ? (keyedError as ErrorCode).code
            : typeof indexedError === "object" && indexedError !== null
              ? (indexedError as ErrorCode).code
              : undefined;

    const fieldProps = Object.assign(
        { ...field },
        {
            name: fieldName,
            disabled: disabled,
            component: undefined,
        }
    ) as InputProps & Record<string, unknown>;

    if (fieldProps.format !== undefined) {
        fieldProps.format = undefined;
    }

    return (
        <React.Fragment>
            {field.type === "hidden" && <Input {...fieldProps} />}
            {field.type !== "hidden" && (
                <div className="field__wrapper">
                    {field.format === undefined && (
                        <CustomComponent
                            inputProps={fieldProps}
                            labelProps={{
                                label: field.label,
                            }}
                        />
                    )}
                    {field.format !== undefined && (
                        <CustomComponent
                            inputProps={fieldProps}
                            labelProps={{
                                label: field.label,
                            }}
                            format={field.format}
                        />
                    )}
                    {hasError && (
                        <Feedback className="field__feedback error">
                            {intl.formatMessage(`error.${errorCode}`)}
                        </Feedback>
                    )}
                </div>
            )}
        </React.Fragment>
    );
};

export interface FieldRepeaterProps {
    fields?: RepeaterFields<unknown>;
    error?: boolean;
    errorMessage?: RepeaterErrorShape;
    labelProps: LabelProps & {
        label: React.ReactNode;
        add?: string;
        delete?: string;
    };
    inputProps: Omit<InputProps, "name"> & {
        name?: InputProps["name"];
        innerFields: Array<RepeaterInnerField>;
        defaultInnerField: unknown;
        maxItems?: number;
    };
    fieldGroupProps?: FieldGroupProps;
}

const FieldRepeater: React.FC<FieldRepeaterProps> = ({
    fields,
    error,
    errorMessage,
    labelProps,
    inputProps,
    fieldGroupProps,
}) => {
    const intl = useContext(IntlContext);
    const disabled = inputProps.disabled || false;
    const required = inputProps.required;
    let title = "";

    if (labelProps !== undefined && labelProps.label !== undefined && labelProps.label !== null) {
        title = labelProps.label.toString();
    }

    return (
        <FieldGroup error={error} {...fieldGroupProps}>
            {labelProps !== undefined && (
                <Label htmlFor={inputProps.id} disabled={disabled} error={error} required={required} {...labelProps}>
                    {labelProps.label}
                </Label>
            )}
            <div className="field__repeater-container">
                <div
                    className={
                        "field__repeater-container--left" +
                        (error ? " error" : "") +
                        (!fields?.length ? " is-empty" : "")
                    }
                    onClick={() => {
                        if (fields && !fields.length) {
                            fields.push(inputProps.defaultInnerField);
                        }
                    }}
                    title={!fields?.length && title !== "" ? title : ""}
                >
                    <div className="field__repeater-fields">
                        {fields?.map((name: string, index: number) => {
                            const topError = errorMessage?.props?.error;
                            const hasError = Array.isArray(topError) ? topError[index] : undefined;
                            return (
                                <div key={name} className={"field__repeater-field" + (hasError ? " error" : "")}>
                                    {inputProps.innerFields.map((innerField: RepeaterInnerField, index: number) => {
                                        return (
                                            <FieldRepeaterItem
                                                key={innerField.key || index}
                                                name={name}
                                                index={index}
                                                field={innerField}
                                                errorMessage={errorMessage}
                                                disabled={disabled}
                                            />
                                        );
                                    })}
                                    {labelProps !== undefined && !disabled && (
                                        <Button
                                            onClick={() => fields.remove(index)}
                                            title={labelProps.delete ?? intl.formatMessage("generic.repeater.delete")}
                                            className="btn btn--icon btn--icon-small btn--red"
                                        >
                                            <Icon icon={{ prefix: "fas", iconName: "times" }} size="2x" />
                                        </Button>
                                    )}
                                    {disabled && (
                                        <Button className="btn btn--icon btn--icon-small btn--disabled">
                                            <Icon icon={{ prefix: "fas", iconName: "minus" }} />
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="field__repeater-container--right">
                    {(inputProps.maxItems === undefined || (fields?.length ?? 0) < inputProps.maxItems) && (
                        <React.Fragment>
                            {labelProps !== undefined && !disabled && (
                                <Button
                                    onClick={() => fields?.push(inputProps.defaultInnerField)}
                                    title={labelProps.add ?? intl.formatMessage("generic.repeater.add")}
                                    className="btn btn--icon btn--secondary"
                                >
                                    <Icon icon={{ prefix: "fas", iconName: "plus" }} />
                                </Button>
                            )}
                            {disabled && (
                                <Button className="btn btn--icon btn--disabled">
                                    <Icon icon={{ prefix: "fas", iconName: "plus" }} />
                                </Button>
                            )}
                        </React.Fragment>
                    )}
                </div>
            </div>

            {typeof errorMessage?.props?.error === "object" &&
                errorMessage?.props?.error !== null &&
                !Array.isArray(errorMessage?.props?.error) &&
                (errorMessage?.props?.error as ErrorCode).code && (
                <Feedback className="field__feedback error">
                    {intl.formatMessage(`error.${(errorMessage?.props?.error as ErrorCode).code}`)}
                </Feedback>
            )}
        </FieldGroup>
    );
};

export default FieldRepeater;
